// EntityPopup — click-into callout for a single IntelSignal.
//
// Per the design doc: 340px wide, anchored to the right of the marker (never
// centered), PIN / COMPARE / OPEN / AOI actions, hover row → 1-line tooltip
// is handled in WorldMap (this component is the click target only).

import { formatRecency } from '@/lib/recency';
import { gdeltCodes, type intel as i } from '@eidollon/contracts';
import type { WarZoneInfo } from '@eidollon/intel-feed-client';
import { signalTypeCssToken } from './colors';

const LABEL: Record<i.SignalType, string> = {
  SAN: 'SANCTIONS',
  CFL: 'CONFLICT',
  SUP: 'SUPPLY',
  OBL: 'OBLIGATION',
  POL: 'POLICY',
  AVI: 'AVIATION',
  SEI: 'SEISMIC',
  WEA: 'WEATHER',
  FIR: 'WILDFIRE',
  ANO: 'ANOMALY',
  NEW: 'NEWS',
};

export interface WarZonePopupProps {
  zone: WarZoneInfo;
  onClose: () => void;
}

export function WarZonePopup({ zone, onClose }: WarZonePopupProps) {
  const sevColor =
    zone.severity >= 90
      ? 'var(--red)'
      : zone.severity >= 75
        ? '#ff6b35'
        : zone.severity >= 60
          ? 'var(--amber)'
          : 'var(--yellow)';
  return (
    <div
      style={{
        position: 'absolute',
        top: 80,
        left: 280,
        width: 340,
        background: 'var(--bg-pane)',
        border: '1px solid var(--primary-line)',
        zIndex: 50,
        fontFamily: 'var(--sans)',
      }}
      // biome-ignore lint/a11y/useSemanticElements: positioned floating popover.
      role="dialog"
      aria-label={`War zone: ${zone.name}`}
    >
      <div
        style={{
          height: 32,
          padding: '0 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderBottom: '1px solid var(--bd)',
          background: 'var(--bg-pane-2)',
        }}
      >
        <span
          style={{ color: sevColor, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 0.5 }}
        >
          WAR ZONE
        </span>
        <span style={{ flex: 1, color: 'var(--fg-2)', fontFamily: 'var(--mono)', fontSize: 11 }}>
          {zone.id.toUpperCase()}
        </span>
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 9,
            letterSpacing: 0.5,
            padding: '1px 5px',
            borderRadius: 2,
            color: sevColor,
            border: `1px solid ${sevColor}`,
          }}
        >
          SEV {zone.severity}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          style={{ color: 'var(--fg-3)', fontSize: 14 }}
        >
          ✕
        </button>
      </div>

      <div style={{ padding: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{zone.name}</div>
        <div style={{ fontSize: 11, color: 'var(--fg-2)', marginBottom: 8 }}>{zone.tag}</div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px 12px',
            fontSize: 11,
            marginTop: 6,
          }}
        >
          <span style={{ color: 'var(--fg-3)' }}>AS OF</span>
          <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg-2)' }}>{zone.asOf}</span>
          <span style={{ color: 'var(--fg-3)' }}>SRC</span>
          <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg-2)' }}>
            UCDP · ACLED · ICG
          </span>
          <span style={{ color: 'var(--fg-3)' }}>CLASS</span>
          <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg-2)' }}>OPEN</span>
        </div>
      </div>
    </div>
  );
}

export interface EntityPopupProps {
  signal: i.IntelSignal;
  onClose: () => void;
  onPin: (s: i.IntelSignal) => void;
  onCompare: (s: i.IntelSignal) => void;
}

export function EntityPopup({ signal, onClose, onPin, onCompare }: EntityPopupProps) {
  const tone = signalTypeCssToken(signal.signal_type);
  const coord = signal.geom ? `${signal.geom.lat.toFixed(3)}, ${signal.geom.lng.toFixed(3)}` : '—';
  const rec = formatRecency(signal.occurred_at);
  return (
    <div
      style={{
        position: 'absolute',
        top: 80,
        left: 280,
        width: 340,
        background: 'var(--bg-pane)',
        border: '1px solid var(--primary-line)',
        zIndex: 50,
        fontFamily: 'var(--sans)',
      }}
      // biome-ignore lint/a11y/useSemanticElements: positioned floating popover.
      role="dialog"
      aria-label={`${LABEL[signal.signal_type]} ${signal.id}`}
    >
      <div
        style={{
          height: 32,
          padding: '0 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderBottom: '1px solid var(--bd)',
          background: 'var(--bg-pane-2)',
        }}
      >
        <span style={{ color: tone, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 0.5 }}>
          {LABEL[signal.signal_type]}
        </span>
        <span style={{ flex: 1, color: 'var(--cyan)', fontFamily: 'var(--mono)', fontSize: 11 }}>
          {signal.id}
        </span>
        <span
          title={rec.absolute}
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 9,
            letterSpacing: 0.5,
            padding: '1px 5px',
            borderRadius: 2,
            ...(rec.isLive
              ? { color: '#06140a', background: '#3fb950', fontWeight: 700 }
              : { color: 'var(--fg-3)', border: '1px solid var(--bd)' }),
          }}
        >
          {rec.isLive ? '● LIVE' : rec.isPast ? `PAST · ${rec.relative}` : rec.relative}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          style={{ color: 'var(--fg-3)', fontSize: 14 }}
        >
          ✕
        </button>
      </div>

      <div style={{ padding: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
          {gdeltCodes.signalTitle(signal)}
        </div>
        <div style={{ fontSize: 11, color: 'var(--fg-2)', marginBottom: 8 }}>
          {signal.source} · {LABEL[signal.signal_type]}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px 12px',
            fontSize: 11,
            marginTop: 6,
          }}
        >
          <span style={{ color: 'var(--fg-3)' }}>SEV</span>
          <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg-1)' }}>{signal.severity}</span>
          <span style={{ color: 'var(--fg-3)' }}>CONF</span>
          <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg-2)' }}>
            {signal.confidence.toFixed(2)}
          </span>
          <span style={{ color: 'var(--fg-3)' }}>WHEN</span>
          <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg-2)' }}>
            {rec.absolute} · {rec.relative}
          </span>
          <span style={{ color: 'var(--fg-3)' }}>COORD</span>
          <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg-2)' }}>{coord}</span>
        </div>

        {signal.linked_opportunity_ids.length > 0 && (
          <>
            <div className="hr" style={{ margin: '10px -12px' }} />
            <div className="h" style={{ marginBottom: 6 }}>
              LINKED OPPS
            </div>
            <div style={{ fontSize: 11, color: 'var(--fg-2)', lineHeight: 1.6 }}>
              {signal.linked_opportunity_ids.slice(0, 4).map((id) => (
                <div key={id} className="mono" style={{ color: 'var(--cyan)' }}>
                  {id}
                </div>
              ))}
              {signal.linked_opportunity_ids.length > 4 && (
                <div style={{ color: 'var(--fg-3)' }}>
                  +{signal.linked_opportunity_ids.length - 4} more
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div
        style={{
          height: 36,
          padding: '0 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          borderTop: '1px solid var(--bd)',
          background: 'var(--bg-pane-2)',
        }}
      >
        <button type="button" className="btn sm" onClick={() => onPin(signal)}>
          PIN
        </button>
        <button type="button" className="btn sm" onClick={() => onCompare(signal)}>
          COMPARE
        </button>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn sm" disabled style={{ opacity: 0.5 }}>
          AOI
        </button>
      </div>
    </div>
  );
}
